import React from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import classNames from 'classnames'
import Wrapper from '@app/components/Common/Wrapper/Wrapper'
import SubDeviceCard from '@app/components/MyDevices/DevicesList/SubDeviceCard/SubDeviceCard'
import CameraCard from '@app/components/MyDevices/DevicesList/CameraCard/CameraCard'
import MezzoLightsCard from '@app/components/MyDevices/DevicesList/MezzoLightsCard/MezzoLightsCard'
import VirtualSwitchesCard from '@app/components/MyDevices/DevicesList/VirtualSwitchesCard/VirtualSwitchesCard'
import MyDevicesCard from '@app/components/MyDevices/DevicesList/DeviceCard/DeviceCard'
import { DeviceTypes, TDevice } from '@app/@types/myDevices'
import useStyles from '../MyDevices.style'

interface SortableItemProps {
  id: string,
  isDragging: boolean,
  children: React.ReactNode,
}

const SortableItem: React.FC<SortableItemProps> = ({ id, isDragging, children }) => {
  const {
    attributes, listeners, setNodeRef, transform, transition,
  } = useSortable({ id, disabled: !isDragging })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  )
}

interface Props {
  devices: Array<TDevice>,
  mezzoLights: number,
  isMobile: boolean,
  isDragging: boolean,
  inProgress: boolean,
  isMezzoLightsLoading: boolean,
  showMezzoLights: boolean,
}

const DevicesList: React.FC<Props> = (props) => {
  const { classes } = useStyles()
  const {
    isDragging, devices, mezzoLights, isMobile, inProgress, isMezzoLightsLoading, showMezzoLights,
  } = props

  const renderDevicesList = (device: TDevice, index: number) => {
    const id = String(device.id ?? index)

    if (device.device_type === DeviceTypes.mezzoLight) {
      if (showMezzoLights) {
        return (
          <SortableItem id={id} isDragging={isDragging} key={`device${index + 1}`}>
            <MezzoLightsCard
              isDragging={isDragging}
              mezzoLights={mezzoLights}
              isMezzoLightsLoading={isMezzoLightsLoading}
              isMobile={isMobile}
            />
          </SortableItem>
        )
      } return null
    } if (device.device_type === DeviceTypes.virtualSwitch) {
      return (
        <SortableItem id={id} isDragging={isDragging} key={`device${index + 1}`}>
          <VirtualSwitchesCard
            isDragging={isDragging}
            isMobile={isMobile}
          />
        </SortableItem>
      )
    } if (device.device_type === DeviceTypes.camera) {
      return (
        <SortableItem id={id} isDragging={isDragging} key={`device${index + 1}`}>
          <CameraCard
            isDragging={isDragging}
            cameraName={device.name}
            isMobile={isMobile}
          />
        </SortableItem>
      )
    } if (device.device_type === DeviceTypes.zwaveSubDevice) {
      return (
        <SortableItem id={id} isDragging={isDragging} key={`device${index + 1}`}>
          <SubDeviceCard
            isDragging={isDragging}
            subDeviceName={device.name}
            parentDevice={device.parent}
            isMobile={isMobile}
          />
        </SortableItem>
      )
    }
    return (
      <SortableItem id={id} isDragging={isDragging} key={`device${index + 1}`}>
        <MyDevicesCard
          isDragging={isDragging}
          device={device}
          isMobile={isMobile}
          deviceType={device.device_type}
          inProgress={inProgress}
        />
      </SortableItem>
    )
  }

  return (
    <Wrapper className={classNames(classes.wrapper, isMobile && classes.wrapperMobile)}>
      {devices.length > 0 && devices.map((device, index) => renderDevicesList(device, index))}
    </Wrapper>
  )
}

export default DevicesList
